import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { config } from "../../../config/config";
import {
  DETAIL_TAB2POINT_CLOUD_TYPE,
  EDisplayType,
  EUpdateStatus,
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { ITextScale } from "../../../viewer/config/ViewerConfig";
import {
  IMeasurement,
  IMeasurementHandler,
  MeasurementType,
} from "../../../viewer/measurement/types";
import { snapshotMeasurementPoints } from "../../../viewer/measurement/lastSavedPointsCache";
import CalcResultDetail from "../Clculation/CalcResultDetail";
import HeatMapDialog from "../Clculation/HeatMapDialog";
import { ProjectVolumeList } from "../Clculation/ProjectVolumeList";
import CalcContext, {
  INITIAL_MEASUREMENT_RESULT_EDIT,
} from "../Contexts/CalcContext";
import { UpdateInfoContext } from "../Contexts/UpdateInfoContext";
import { debounce } from "lodash";
import { Box } from "@mui/material";
import axios from "axios";
import {
  finishMeasurementResultEdit,
  startMeasurementResultEdit,
} from "../../../viewer/measurement/measurementResultPanelEdit";
import {
  cancelMeasurementResultPosition,
  confirmMeasurementResultPosition,
  resetMeasurementResultPosition,
} from "../../../viewer/measurement/measurementResultPanelCommands";
import { IMeasurementPanelPosition } from "../../../viewer/measurement/types";
import { patchMeasurementPanelPosition } from "../../../api/measurementPanelPositionApi";
import { toMeasurementUploadPayload } from "../../../viewer/measurement/measurementUploadPayload";

type Props = {};

export type VolumeCalcColumn = {
  id: string;
  pointName: string;
  x: number;
  y: number;
  z: number;
};

export const CalcDrawerContent = memo((props: Props) => {
  //language
  const { t } = useTranslation();
  const {
    viewer,
    currentViewerMode,
    currentPointCloud,
    projectDetail,
    detailTab,
  } = useContext(ProjectDetailContext);
  const {
    measurementType,
    setMeasurementType,
    setOnEditingMode,
    onEditingMode,
    measurementResultEdit,
    setMeasurementResultEdit,
  } = useContext(CalcContext);
  const { displayType } = useContext(ProjectDetailContext);
  const { setUpdateAt, updateAt } = useContext(UpdateInfoContext);

  /*****************************************
      state
    ******************************************/
  const [openCalcResultDetailDialog, setOpenCalcResultDetailDialog] =
    useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IMeasurementHandler>();
  const [editedItem, setEditedItem] = useState<IMeasurementHandler>();
  const [selectedCalcResult, setSelectedCalcResult] = useState<
    IMeasurementHandler[]
  >([]);
  const [rowsVolumeCalc, setRowsVolumeCalc] = useState<VolumeCalcColumn[]>([]);
  // "Edit (Measurement Result)" state (SC_QUICK-2187, QU3DW-1367)
  const [panelPositionBeforeEdit, setPanelPositionBeforeEdit] =
    useState<IMeasurementPanelPosition>();
  const [panelPositionSaving, setPanelPositionSaving] =
    useState<boolean>(false);
  /** Measurement whose panel edit session is currently open, if any. */
  const panelEditSessionIdRef = useRef<number>();
  const [showHeatMapBtn, setShowHeatMapBtn] = useState<boolean>(false);
  const [editType, setEditType] = useState<string>("PICK_POINT");
  const [showHeatMap, setShowHeatMap] = useState<boolean>(false);
  const [beforeUpdateAt, setBeforeUpdateAt] = useState<string>(updateAt);

  const editTypeItems = [
    {
      label: t("PICK_POINT"),
      value: "PICK_POINT",
    },
    {
      label: t("MOVE_XY"),
      value: "MOVE_XY",
    },
    {
      label: t("MOVE_Z"),
      value: "MOVE_Z",
    },
  ];

  /*****************************************
      Effect
    ******************************************/
  useEffect(() => {
    const abortController = new AbortController();
    const abortControllerSignal = abortController.signal;

    if (currentViewerMode === EViewerMode.CALCULATION) {
      setCalcResultList(abortControllerSignal);
    }

    return () => {
      abortController.abort();
      selectedItem?.id && viewer?.setHeatMapVisibility(selectedItem?.id, false);
      viewer?.deleteMeasurement();
      finishMeasurement();
      setOpenCalcResultDetailDialog(false);
      setSelectedItem(null);
      setSelectedCalcResult([]);
      setMeasurementType(null);
    };
  }, [currentViewerMode, currentPointCloud]);

  useEffect(() => {
    if (currentViewerMode === EViewerMode.CALCULATION) {
      measurementType ? startMeasurement() : finishMeasurement();
    } else {
      selectedItem?.id && viewer?.setHeatMapVisibility(selectedItem?.id, false);
      viewer?.deleteMeasurement();
      finishMeasurement();
      setOpenCalcResultDetailDialog(false);
      setSelectedItem(null);
      setSelectedCalcResult([]);
      setMeasurementType(null);
    }
    return () => {};
  }, [measurementType]);

  // useEffect(() => {
  //   return () => {
  //     uploadCalcResult(selectedCalcResult);
  //   };
  // }, [currentCalculationMode]);

  useEffect(() => {
    if (currentViewerMode === EViewerMode.CALCULATION && !onEditingMode) {
      startMeasurement();
    }
  }, [selectedCalcResult.length, onEditingMode]);

  useEffect(() => {
    const shouldStart = !onEditingMode;

    if (shouldStart) startMeasurement();

    return () => {
      if (shouldStart) finishMeasurement();
    };
  }, [onEditingMode]);

  useEffect(() => {
    selectedCalcResult.map((result) => {
      if (result?.type === MeasurementType.VOLUME_CALCULATION) {
        if (result.id !== selectedItem?.id) {
          viewer?.setHeatMapVisibility(result?.id, false);
        }
      }
    });

    if (showHeatMap) {
      viewer?.setHeatMapVisibility(selectedItem?.id, true);
    }

    setShowHeatMapBtn(
      selectedItem?.type === MeasurementType.VOLUME_CALCULATION
    );

    return () => {
      setShowHeatMapBtn(false);
      // console.log(selectedItem?.id);
      viewer?.setHeatMapVisibility(selectedItem?.id, false);
    };
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem?.getVisibility()) {
      viewer?.setHeatMapVisibility(selectedItem?.id, showHeatMap);
    }
  }, [showHeatMap]);

  /*****************************************
      function
    ******************************************/
  // heatMap表示ボタン
  const toggleSetShowHeatMap = (show?: boolean) => {
    setShowHeatMap(show === undefined ? !showHeatMap : show);
  };

  /**
   * Closes the "Edit (Measurement Result)" session (SC_QUICK-2187).
   * Must run on every exit path — including deleting a measurement — otherwise
   * the toolbar keeps thinking a panel edit is in progress and leaves X
   * enabled with nothing to cancel (QU3DW-1392).
   */
  const resetPanelEditSession = () => {
    panelEditSessionIdRef.current = undefined;
    setPanelPositionBeforeEdit(undefined);
    setPanelPositionSaving(false);
    setMeasurementResultEdit(INITIAL_MEASUREMENT_RESULT_EDIT);
  };

  // 体積計算のピック方法切替
  const handlePickType = (v: string, selected?: IMeasurementHandler) => {
    setEditType(v);
    if (onEditingMode) {
      finishEdit();
      startEdit(v, selected);
    }
  };

  // 編集ボタン
  // const toggleEditBtn = () => {
  //   if (currentCalculationMode === ECalculationMode.EDITING) {
  //     setCurrentCalculationMode(ECalculationMode.CREATING);
  //   } else {
  //     setCurrentCalculationMode(ECalculationMode.EDITING);
  //   }
  // };

  // 編集開始
  const startEdit = (type?: string, selected?: IMeasurementHandler) => {
    setOnEditingMode(true);
    const editId = selected ? selected.id : selectedItem?.id;

    viewer.setMeasurementVisibility(editId, {
      text: true,
      measurement: true,
    });
    if (!type) type = editType;

    if (type === "MOVE_PANEL") {
      // "Edit (Measurement Result)" (SC_QUICK-2187, QU3DW-1367): drag the
      // Measurement Result panel, not the measurement points. Uses the
      // panel-drag API instead of viewer.editMeasurement (AC-3, AC-4).
      //
      // Leave measurement-creation mode first. The other edit types leave it
      // implicitly via viewer.editMeasurement (which sets isMeasuring); this
      // branch never calls it, so without this the still-running creation
      // preview would treat the drag as drawing a new measurement and a
      // second result panel would appear. PanelDragObservable also refuses to
      // drag while isMeasuring is true.
      viewer.cancelMeasurement();
      viewer.finishMeasurement();
      setMeasurementType(null);

      // Only capture the "position before edit" when a NEW edit session starts
      // for this measurement. startEdit is re-entered on re-render (see the
      // effect in CalcResultDetail); overwriting the baseline there would move
      // it to the already-dragged position and Tick would never activate
      // (QU3DW-1390).
      if (panelEditSessionIdRef.current !== editId) {
        panelEditSessionIdRef.current = editId;
        const currentPosition = viewer.getPanelPosition(editId);
        setPanelPositionBeforeEdit(
          currentPosition
            ? {
                x: currentPosition.x,
                y: currentPosition.y,
                z: currentPosition.z,
              }
            : undefined
        );
      }
      startMeasurementResultEdit(viewer, editId);
      // Hand the toolbar Tick/X over to the panel-position save/cancel
      // instead of the default measurement-edit behaviour. The handlers
      // themselves are (re)registered by the effect below, so they always
      // close over the current panelPositionBeforeEdit.
      setMeasurementResultEdit({
        ...INITIAL_MEASUREMENT_RESULT_EDIT,
        active: true,
      });
      viewer.updateRenderer();
      return;
    }

    switch (type) {
      case editTypeItems[0].value:
        viewer.editMeasurement(
          editId,
          { direction: "default", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
      case editTypeItems[1].value:
        viewer.editMeasurement(
          editId,
          { direction: "xy", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
      case editTypeItems[2].value:
        viewer.editMeasurement(
          editId,
          { direction: "z", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
      default:
        viewer.editMeasurement(
          editId,
          { direction: "default", dataflow: "latest_val_on_release" },
          [{ onUpdateComplete: onPositionUpdate }]
        );
        break;
    }

    viewer.setHeatMapVisibility(selectedItem?.id, showHeatMap);
    viewer.updateRenderer();
  };

  // 編集終了
  const finishEdit = () => {
    if (!editedItem) return;

    setOnEditingMode(false);

    if (editType === "MOVE_PANEL") {
      // AC-19: leaving "Edit (Measurement Result)" disables panel dragging
      // again — view controls (pan/rotate) are unaffected either way, since
      // they were never disabled by entering this mode (see
      // startMeasurementResultEdit / PanelDragObservable).
      //
      // Deliberately does NOT reset editType: handlePickType sets it before
      // calling finishEdit, so resetting here would overwrite the type the
      // user just picked and the next edit session would silently fall back
      // to point editing (QU3DW-1391).
      finishMeasurementResultEdit(viewer);
      resetPanelEditSession();
      return;
    }

    viewer.updateEditMeasurement(projectDetail.id);
    viewer.finishMeasurement();
  };

  // Keeps the toolbar Tick/X (CalcTabPanel) in sync with the live panel
  // position: Tick only becomes active once the panel actually moved (VR-1).
  const handlePanelPositionChange = (changed: boolean) => {
    setMeasurementResultEdit((prev) =>
      prev.changed === changed ? prev : { ...prev, changed }
    );
  };

  // Safety net for QU3DW-1392: once there is no measurement left (or none
  // selected), there is nothing to confirm or cancel — close any panel edit
  // session so the toolbar X/Tick return to disabled.
  useEffect(() => {
    if (!measurementResultEdit.active) return;
    if (selectedCalcResult.length > 0 && selectedItem) return;

    finishMeasurementResultEdit(viewer);
    resetPanelEditSession();
  }, [selectedCalcResult.length, selectedItem, measurementResultEdit.active]);

  // Re-register the toolbar Tick/X handlers whenever the state they close
  // over changes, so they never act on a stale panelPositionBeforeEdit.
  useEffect(() => {
    setMeasurementResultEdit((prev) =>
      prev.active
        ? {
            ...prev,
            saving: panelPositionSaving,
            onConfirm: handleConfirmPanelPosition,
            onCancel: handleCancelPanelPosition,
          }
        : prev
    );
  }, [
    measurementResultEdit.active,
    selectedItem,
    panelPositionBeforeEdit,
    panelPositionSaving,
    detailTab,
  ]);

  // Reset icon (AC-8): move the panel back to the measurement's first point.
  // Does not save — only Tick persists a change (BR-2).
  const handleResetPanelPosition = () => {
    if (!selectedItem) return;
    resetMeasurementResultPosition(viewer, selectedItem.id);
    viewer.updateRenderer();
  };

  // Button X (AC-6, AC-9): always restore the pre-Edit position, even after Reset.
  const handleCancelPanelPosition = () => {
    if (!selectedItem || !panelPositionBeforeEdit) return;
    cancelMeasurementResultPosition(viewer, selectedItem.id, panelPositionBeforeEdit);
    viewer.updateRenderer();
    finishEdit();
  };

  // Button Tick (AC-5, AC-10, AC-11, AC-12, AC-14): save immediately; on
  // failure the panel rolls back to the pre-Edit position (BR-11).
  const handleConfirmPanelPosition = async () => {
    if (!selectedItem || !panelPositionBeforeEdit) return;

    setPanelPositionSaving(true);
    const result = await confirmMeasurementResultPosition(
      viewer,
      selectedItem.id,
      panelPositionBeforeEdit,
      (id, position) =>
        patchMeasurementPanelPosition(
          config.serverHost,
          projectDetail.id,
          DETAIL_TAB2POINT_CLOUD_TYPE(detailTab),
          id,
          position
        )
    );
    setPanelPositionSaving(false);

    if (result.success) {
      if (result.updateAt) setUpdateAt(result.updateAt);
      finishEdit();
    }
    // On failure: viewer already rolled back by confirmMeasurementResultPosition.
    // Stay in edit mode so the user can retry Tick.
  };

  const onPositionUpdate = () => {
    const offset = viewer.getOffset();
    const pointsList = selectedItem?._points.map((data: any, index: any) => {
      return {
        id: index,
        pointName: index + 1,
        x: Number((data.x - offset.x).toFixed(3)),
        y: Number((data.y - offset.y).toFixed(3)),
        z: Number((data.z - offset.z).toFixed(3)),
      };
    }) as VolumeCalcColumn[];
    setRowsVolumeCalc(pointsList);
  };

  // 計測開始
  const startMeasurement = async () => {
    const textScale: ITextScale =
      displayType === EDisplayType.iOS
        ? { orthographic: 0.04, perspective: 0.12 }
        : { orthographic: 0.09, perspective: 0.18 };

    if (measurementType === MeasurementType.SURFACE) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.SURFACE },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.FLAT_AREA_CALCULATION) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.FLAT_AREA_CALCULATION },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.HORIZONTAL) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.HORIZONTAL },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.VERTICAL) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.VERTICAL },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.TWO_POINT) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.TWO_POINT },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.MULTIPLE_POINT) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          { type: MeasurementType.MULTIPLE_POINT },
          textScale
        )
      );
    } else if (measurementType === MeasurementType.VOLUME_CALCULATION) {
      addSelectedCalcResult(
        await viewer.createMeasurement(
          {
            type: MeasurementType.VOLUME_CALCULATION,
            lowerBound: {
              minColor: 0xa5e5ff,
              maxColor: 0x0000ff,
            },
            upperBound: {
              minColor: 0xffff00,
              maxColor: 0xff0000,
            },
          },
          textScale
        )
      );
    }
  };

  // 計測終了
  const finishMeasurement = async () => {
    viewer.cancelMeasurement();
    viewer.finishMeasurement();
  };

  const addSelectedCalcResult = (data: IMeasurementHandler) => {
    if (data?.id === 1) {
      uploadCalcResult([data]);
      setSelectedCalcResult([data]);
    } else {
      uploadCalcResult([...selectedCalcResult, data]);
      setSelectedCalcResult([...selectedCalcResult, data]);
    }

    setSelectedItem(data);
    setOpenCalcResultDetailDialog(true);
  };

  const initMeasurement = async (abortControllerSignal: AbortSignal) => {
    if (selectedCalcResult.length <= 0) {
      const measurements = await setCalcResultList(abortControllerSignal);
    }
  };

  const setCalcResultList = async (abortControllerSignal: AbortSignal) => {
    try {
      viewer.setMeasurementId(0);
      viewer.initMeasurementMap();

      const apiUrl = `${config.serverHost}/api/v1/project/${
        projectDetail.id
      }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/measurement`;

      const response = await axios.get(apiUrl, {
        withCredentials: true,
        signal: abortControllerSignal,
      });

      const measurements: IMeasurementHandler[] = response.data;
      const measurementsTmp: IMeasurementHandler[] = [];

      const textScale: ITextScale =
        displayType === EDisplayType.iOS
          ? { orthographic: 0.04, perspective: 0.12 }
          : { orthographic: 0.09, perspective: 0.18 };

      for (const val of measurements) {
        viewer.poligonIndicesCount = 0;
        if (val._points.length > 0) {
          const initialMeasurement = await viewer.createInitialMeasurement(
            { type: val.type },
            val,
            textScale
          );
          initMeasurement && measurementsTmp.push(initialMeasurement);
        }
      }

      setSelectedCalcResult(measurementsTmp);
      return measurements;
    } catch (e) {
      // console.error(e);
      return [];
    }
  };

  const uploadCalcResult = async (calcArray: IMeasurementHandler[]) => {
    const afterUpdateAt = new Date().toISOString();

    const calcNewArray: IMeasurement[] = toMeasurementUploadPayload(
      calcArray,
      viewer,
      afterUpdateAt
    );

    const savedSnapshot = snapshotMeasurementPoints(calcArray);
    setUpdateAt(afterUpdateAt);

    await axios
      .put(
        `${config.serverHost}/api/v1/project/${
          projectDetail.id
        }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/measurement`,
        { measurements: calcNewArray },
        { withCredentials: true }
      )
      .then(() => {
        viewer.recordSavedMeasurements(savedSnapshot);
        setUpdateAt("saved");

        setTimeout(() => {
          setUpdateAt(afterUpdateAt);
        }, 3000);
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 403) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_FORBIDDEN);
          } else if (err.response.status === 500) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_ERROR);
          } else {
            setUpdateAt(EUpdateStatus.SAVE_FAILED);
          }
        }

        setTimeout(() => {
          setUpdateAt(beforeUpdateAt);
        }, 3000);
      });
  };

  const deleteCalcResult = async (deleteId: number) => {
    const calcNewArray: IMeasurement[] = [];
    const calcHandlerNewArray: IMeasurementHandler[] = [];

    // The measurement being panel-edited is going away — close the session so
    // the toolbar X/Tick go back to disabled (QU3DW-1392).
    if (panelEditSessionIdRef.current === deleteId) {
      finishMeasurementResultEdit(viewer);
      resetPanelEditSession();
    }

    viewer?.setHeatMapVisibility(deleteId, false);
    viewer?.deleteMeasurement(deleteId);

    // clear selected item and set mode to creating if the item is being edited
    if (
      (selectedItem?.id === deleteId &&
        onEditingMode &&
        editedItem?.id === selectedItem?.id) ||
      editedItem?.id === deleteId
    ) {
      setOnEditingMode(false);
      setSelectedItem(null);

      setEditedItem(null);
    }

    const afterUpdateAt = new Date().toISOString();

    for (const val of selectedCalcResult) {
      if (val.id !== deleteId) {
        calcHandlerNewArray.push(val);
      }
    }

    // Same as uploadCalcResult: keep panelPosition so deleting one measurement
    // does not reset the panel positions of the remaining ones.
    calcNewArray.push(
      ...toMeasurementUploadPayload(calcHandlerNewArray, viewer, afterUpdateAt)
    );

    setUpdateAt(afterUpdateAt);

    // changePickedMeasurementTexture()

    setSelectedCalcResult(calcHandlerNewArray);

    const savedSnapshot = snapshotMeasurementPoints(calcHandlerNewArray);

    await axios
      .put(
        `${config.serverHost}/api/v1/project/${
          projectDetail.id
        }/point-cloud/${DETAIL_TAB2POINT_CLOUD_TYPE(detailTab)}/measurement`,
        { measurements: calcNewArray },
        { withCredentials: true }
      )
      .then(() => {
        viewer.recordSavedMeasurements(savedSnapshot);
        setUpdateAt("saved");

        setTimeout(() => {
          setUpdateAt(afterUpdateAt);
        }, 3000);
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.status === 403) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_FORBIDDEN);
          } else if (err.response.status === 500) {
            setUpdateAt(EUpdateStatus.SAVE_FAILED_ERROR);
          } else {
            setUpdateAt(EUpdateStatus.SAVE_FAILED);
          }
        }

        setTimeout(() => {
          setUpdateAt(beforeUpdateAt);
        }, 3000);
      });
  };

  const uploadCalcResultDebounce = useCallback(
    debounce((calcArray: IMeasurementHandler[]) => {
      uploadCalcResult(calcArray);
    }, 1000),
    [detailTab]
  );

  const handleSetSelectedCalcResult = (calcArray: IMeasurementHandler[]) => {
    setSelectedCalcResult(calcArray);
    uploadCalcResultDebounce(calcArray);
  };

  // UI
  // 計測詳細閉じるボタン
  const handleClickCancel = () => {
    setOpenCalcResultDetailDialog(false);
    setSelectedItem(null);
    viewer.changeAppearanceBySelected(null);
  };

  const resCalcType = (data: any) => {
    if (!data) return;
    const type: string = data.type;
    if (type === MeasurementType.VOLUME_CALCULATION) {
      return t("VOLUME");
    } else if (type === MeasurementType.TWO_POINT) {
      return t("DISTANCE_TWO_POINTS");
    } else if (type === MeasurementType.MULTIPLE_POINT) {
      return t("DISTANCE_MULTIPLE_POINTS");
    } else if (type === MeasurementType.VERTICAL) {
      return t("VERTICAL_DISTANCE");
    } else if (type === MeasurementType.HORIZONTAL) {
      return t("HORIZONTAL_DISTANCE");
    } else if (type === MeasurementType.FLAT_AREA_CALCULATION) {
      return t("FLAT_AREA");
    } else if (type === MeasurementType.SURFACE) {
      return t("SURFACE_DISTANCE");
    }
  };

  const setSelectedItemFromList = (any: any) => {
    console.log("setSelectedItemFromList", any);

    setSelectedItem(any);
  };

  return (
    <Box
      style={{
        display: "flex",
        padding: "4px 16px",
        height: `calc(100% - 8px)`,
        gap: "var(--space-space--4, 4px)",
      }}
    >
      <ProjectVolumeList
        setOpenCalcResultDetailDialog={setOpenCalcResultDetailDialog}
        setSelectedItem={setSelectedItem}
        setEditedItem={setEditedItem}
        openCalcResultDetailDialog={openCalcResultDetailDialog}
        resCalcType={resCalcType}
        selectedCalcResult={selectedCalcResult}
        setSelectedCalcResult={setSelectedCalcResult}
        selectedItem={selectedItem}
        handleClickCancel={handleClickCancel}
        handleSetSelectedCalcResult={handleSetSelectedCalcResult}
        deleteCalcResult={deleteCalcResult}
        showHeatMap={showHeatMap}
        handlePickType={handlePickType}
        toggleSetShowHeatMap={toggleSetShowHeatMap}
      />
      <CalcResultDetail
        selectedItem={selectedItem}
        handleClickCancel={handleClickCancel}
        showHeatMap={showHeatMap}
        rowsVolumeCalc={rowsVolumeCalc}
        setRowsVolumeCalc={setRowsVolumeCalc}
        startEdit={startEdit}
        finishEdit={finishEdit}
        isEditingMeasurementResult={onEditingMode && editType === "MOVE_PANEL"}
        panelPositionBeforeEdit={panelPositionBeforeEdit}
        panelPositionSaving={panelPositionSaving}
        onResetPanelPosition={handleResetPanelPosition}
        onPanelPositionChange={handlePanelPositionChange}
      />
      {showHeatMap &&
        selectedItem?.type === MeasurementType.VOLUME_CALCULATION && (
          <HeatMapDialog item={selectedItem} />
        )}
    </Box>
  );
});
